import { useState, type ReactNode } from 'react';
import {
  radia,
  zigbee,
  type PosudokKanala,
  type PrehladRadii,
  type SeriovyKlucRadia,
  type SluzbaRadia,
  type StavAgentaRadii,
  type StavThreadu,
  type VarovanieRadii,
} from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { EmptyState } from '../components/states.js';
import {
  Badge,
  Button,
  Card,
  DataRow,
  Field,
  Notice,
  PageHeader,
  SectionTitle,
} from '../components/ui.js';
import { IconRefresh } from '../components/icons.js';
import { useSession } from '../auth/session.js';
import { Data, farbaTonu } from './common.js';
import { MeranieKanalov } from './radia-meranie.js';

/*
 * Radia.
 *
 * Zigbee aj Thread pracuju v pasme 2,4 GHz (802.15.4, kanaly 11 az 26)
 * a k tomu tam vysiela Wi-Fi. Tato obrazovka odpoveda na tri otazky:
 * co je zapichnute, co to pouziva a ci si to nelezi.
 *
 * Posudky kanalov pocita SERVER - su to pravidla pasma a maju vlastne
 * testy. Tu sa len zobrazuju.
 */

const NAZOV_SLUZBY: Record<SluzbaRadia, string> = {
  zigbee2mqtt: 'Zigbee',
  otbr: 'Thread',
};

/** Rola uzla v Thread sieti; `disabled` neznamena poruchu. */
const POPIS_STAVU_THREADU: Record<string, string> = {
  disabled: 'Zásobník je vypnutý',
  detached: 'Mimo siete',
  child: 'Pripojený ako child',
  router: 'Router',
  leader: 'Leader siete',
};

const TON_STAVU_THREADU: Record<string, 'ok' | 'warn' | 'neutral'> = {
  disabled: 'neutral',
  detached: 'warn',
  child: 'ok',
  router: 'ok',
  leader: 'ok',
};

/** Hodnota, ktorou sa border router prepne na simulovane radio. */
const SIMULACIA = 'simulacia';

export function Radios(): ReactNode {
  const nacitanie = useApi<PrehladRadii>((signal) => radia.prehlad(signal), []);
  const { user } = useSession();
  const jeSpravca = user?.role === 'admin';
  const [prepina, setPrepina] = useState<string | undefined>(undefined);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  /*
   * Na co sa prave pytame. Prepnutie radia znovu vytvori kontajner
   * sluzby, takze siet je na niekolko sekund mimo - to nie je nieco, co
   * sa ma stat jednym kliknutim omylom.
   */
  const [pyta, setPyta] = useState<{ sluzba: SluzbaRadia; zariadenie: string } | undefined>(
    undefined,
  );

  /*
   * Prepnutie radia.
   *
   * Vykonava ho agent na hostitelovi, takze odpoved znamena len „zapisane".
   * Preto sa po nej caka: sluzba sa znovu vytvara a kym nabehne, prehlad
   * by ukazoval stary stav.
   */
  const prepni = (sluzba: SluzbaRadia, zariadenie: string): void => {
    setChyba(undefined);
    setPyta(undefined);
    setPrepina(`${sluzba}:${zariadenie}`);
    void radia
      .prepni(sluzba, zariadenie)
      .then(() => new Promise((r) => setTimeout(r, 8000)))
      .then(() => nacitanie.refresh())
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Rádio sa nepodarilo prepnúť.');
      })
      .finally(() => setPrepina(undefined));
  };

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader
        title="Rádiá"
        detail="Zigbee a Thread zdieľajú pásmo 2,4 GHz s Wi-Fi. Tu je vidno, čo kde beží."
        action={
          <Button
            onClick={nacitanie.reload}
            icon={<IconRefresh className="size-4" />}
            variant="ghost"
          >
            Obnoviť
          </Button>
        }
      />

      <Data nacitanie={nacitanie}>
        {(data) => (
          <div className="flex min-w-0 flex-col gap-6">
            {data.varovania.length > 0 && <Varovania varovania={data.varovania} />}

            {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

            {pyta !== undefined && (
              <Potvrdenie
                volba={pyta}
                kluce={data.kluce}
                prebieha={prepina !== undefined}
                naPotvrdenie={() => prepni(pyta.sluzba, pyta.zariadenie)}
                naZrusenie={() => setPyta(undefined)}
              />
            )}

            <KartaAgenta stav={data.agent} sviezi={data.vysledokSviezi} />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <KartaZigbee
                zigbee={data.zigbee}
                threadKanal={threadKanal(data.thread)}
                jeSpravca={jeSpravca}
                naObnovu={() => nacitanie.refresh()}
              />
              <KartaThreadu
                thread={data.thread}
                zigbeeKanal={data.zigbee.channel}
                jeSpravca={jeSpravca}
                rozhrania={data.rozhrania}
                infraIf={data.agent.kind === 'znamy' ? data.agent.infraIf : null}
                naObnovu={() => nacitanie.refresh()}
              />
            </div>

            {/*
              Meranie stojí samostatne, nie v karte jedného rádia:
              meria sa jedno pásmo a výsledok platí pre obe siete.
            */}
            <MeranieKanalov
              zigbeeKanal={data.zigbee.channel}
              threadKanal={threadKanal(data.thread)}
              jeSpravca={jeSpravca}
              dostupne={data.thread.kind === 'znamy'}
            />

            <section className="flex min-w-0 flex-col gap-3">
              <SectionTitle>Pripojené rádiá ({data.kluce.length})</SectionTitle>
              {data.kluce.length === 0 ? (
                <EmptyState
                  title="Žiadne USB rádio"
                  detail="Zoznam sa číta z /dev/serial/by-id. Keď je prázdny, buď nie je zapichnuté žiadne rádio, alebo priečinok nie je pripojený do kontajnera."
                />
              ) : (
                <div className="flex min-w-0 flex-col gap-2">
                  {data.kluce.map((kluc) => (
                    <RiadokKluca
                      key={kluc.nazov}
                      kluc={kluc}
                      jeSpravca={jeSpravca}
                      mozePrepnut={data.agent.kind === 'znamy' && data.agent.prebieha === null}
                      prepina={prepina}
                      naVyber={(s, z) => setPyta({ sluzba: s, zariadenie: z })}
                    />
                  ))}
                </div>
              )}
            </section>

            <KartaSimulacie
              thread={data.thread}
              agent={data.agent}
              jeSpravca={jeSpravca}
              prepina={prepina}
              naVyber={(s, z) => setPyta({ sluzba: s, zariadenie: z })}
            />
          </div>
        )}
      </Data>
    </div>
  );
}

/**
 * Rozhranie do domacej siete pre border router.
 *
 * Pri simulovanom radiu staci slucka. So skutocnym nie: na `lo` by
 * Thread bezal, ale zariadenia by z LAN nikto nedosiahol. Nastavuje sa
 * odtialto, nie rucne v `.env` - agent ho na hostitelovi prepise
 * a border router znovu vytvori.
 */
function RozhranieThreadu({
  rozhrania,
  infraIf,
  naObnovu,
}: {
  readonly rozhrania: readonly string[];
  readonly infraIf: string | null;
  readonly naObnovu: () => void;
}): ReactNode {
  const [otvorene, setOtvorene] = useState(false);
  const [vyber, setVyber] = useState<string | undefined>(undefined);
  const [bezi, setBezi] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  // Predvoli sa prve rozhranie, ktore nie je slucka - typicky to spravne.
  const zvolene = vyber ?? infraIf ?? rozhrania.find((r) => r !== 'lo') ?? rozhrania[0] ?? 'lo';

  const posli = (): void => {
    setChyba(undefined);
    setBezi(true);
    void radia
      .threadRozhranie(zvolene)
      .then(() => new Promise((r) => setTimeout(r, 6000)))
      .then(() => {
        setOtvorene(false);
        naObnovu();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Rozhranie sa nepodarilo nastaviť.');
      })
      .finally(() => setBezi(false));
  };

  if (!otvorene) {
    return (
      <div>
        <Button variant="ghost" onClick={() => setOtvorene(true)}>
          Zmeniť rozhranie do siete
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-(--radius-control) bg-(--color-surface-sunken) p-3">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="thread-infra" className="text-sm font-medium text-(--color-text)">
          Rozhranie do domácej siete
        </label>
        <select
          id="thread-infra"
          value={zvolene}
          onChange={(e) => setVyber(e.target.value)}
          className="w-full rounded-(--radius-control) border border-(--color-border-strong) bg-(--color-surface) px-3 py-2 text-sm text-(--color-text)"
        >
          {rozhrania.map((r) => (
            <option key={r} value={r}>
              {r}
              {r === 'lo' ? ' — slučka, len pre simuláciu' : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-(--color-text-subtle)">
          Cez toto rozhranie border router ohlasuje Thread služby do domácej siete. So skutočným
          rádiom to musí byť rozhranie, ktorým brána vidí LAN.
        </p>
      </div>

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

      <div className="flex flex-wrap gap-2">
        <Button onClick={posli} disabled={bezi || zvolene === infraIf}>
          {bezi ? 'Nastavujem…' : 'Nastaviť'}
        </Button>
        <Button variant="ghost" onClick={() => setOtvorene(false)} disabled={bezi}>
          Nechať
        </Button>
      </div>
    </div>
  );
}

/**
 * Zapnutie a vypnutie zasobnika Thread.
 *
 * Nie je to kozmetika: aktivny dataset otbr-agent prijme LEN od
 * vypnuteho uzla, takze bez tohoto by sa siet z rozhrania nedala
 * zalozit vzdy - napriklad ked uzol visi v stave `detached`.
 */
function ZasobnikThreadu({
  stav,
  naObnovu,
}: {
  readonly stav: 'disabled' | 'detached' | 'child' | 'router' | 'leader';
  readonly naObnovu: () => void;
}): ReactNode {
  const [bezi, setBezi] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const vypnuty = stav === 'disabled';

  const prepni = (): void => {
    setChyba(undefined);
    setBezi(true);
    void radia
      .threadStav(vypnuty ? 'enable' : 'disable')
      .then(() => naObnovu())
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Stav sa nepodarilo zmeniť.');
      })
      .finally(() => setBezi(false));
  };

  return (
    <div className="flex flex-col gap-2">
      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
      <div>
        <Button variant="ghost" onClick={prepni} disabled={bezi}>
          {bezi ? 'Mením…' : vypnuty ? 'Zapnúť zásobník' : 'Vypnúť zásobník'}
        </Button>
      </div>
      {!vypnuty && (
        <p className="text-xs text-(--color-text-muted)">
          Vypnutím sa Thread sieť zastaví — zariadenia v nej prestanú byť dostupné. Dataset zostáva
          uložený, takže po zapnutí sieť pokračuje.
        </p>
      )}
    </div>
  );
}

/**
 * Zalozenie siete Thread a zmena jej kanala.
 *
 * Dve rozne operacie podla toho, ci siet uz bezi:
 *
 *   nebezi -> zaklada sa, teda meno aj kanal, a plati hned
 *   bezi   -> meni sa kanal cez cakajuci dataset, takze vsetky uzly
 *             ho prevezmu naraz az po casovaci
 *
 * Co sa posiela, je len to, co sa meni. Sietovy kluc sa necita ani
 * nezapisuje - zostava tam, kde patri.
 */
function SietThreadu({
  maSiet,
  kanal,
  zigbeeKanal,
  naObnovu,
}: {
  readonly maSiet: boolean;
  readonly kanal: number | undefined;
  readonly zigbeeKanal: number | null;
  readonly naObnovu: () => void;
}): ReactNode {
  const [otvorene, setOtvorene] = useState(false);
  const [vyber, setVyber] = useState<number | undefined>(undefined);
  const [nazov, setNazov] = useState('LoxLink-Thread');
  const [bezi, setBezi] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [hlaska, setHlaska] = useState<string | undefined>(undefined);
  const [pytaBlizko, setPytaBlizko] = useState(false);

  const zvoleny = vyber ?? kanal ?? odporucenyKanal(zigbeeKanal);
  const blizko = zigbeeKanal !== null && Math.abs(zvoleny - zigbeeKanal) < 3;

  const posli = (potvrdeneBlizko: boolean): void => {
    setChyba(undefined);
    setHlaska(undefined);
    setBezi(true);
    void radia
      .threadSiet({
        kanal: zvoleny,
        ...(maSiet ? {} : { nazov }),
        ...(potvrdeneBlizko ? { potvrdeneBlizko: true } : {}),
      })
      .then((v) => {
        setPytaBlizko(false);
        setOtvorene(false);
        setHlaska(
          v.nezname === true
            ? 'Leader siete neodpovedal do pol minúty. Zmena sa ešte môže uplatniť — pozri stav o chvíľu.'
            : v.odkladMs !== undefined && v.odkladMs > 0
              ? `Zmena je zaregistrovaná. Sieť ju prevezme za ${Math.round(v.odkladMs / 1000)} sekúnd.`
              : 'Hotovo.',
        );
        naObnovu();
      })
      .catch((error: unknown) => {
        const sprava = error instanceof Error ? error.message : 'Zmenu sa nepodarilo zapísať.';
        // Server odmieta kanál blízko Zigbee, kým to niekto nepotvrdí.
        if (blizko && !pytaBlizko) setPytaBlizko(true);
        setChyba(sprava);
      })
      .finally(() => setBezi(false));
  };

  if (!otvorene) {
    return (
      <div className="flex flex-col gap-2">
        {hlaska !== undefined && <Notice tone="ok">{hlaska}</Notice>}
        <div>
          <Button variant="ghost" onClick={() => setOtvorene(true)}>
            {maSiet ? 'Zmeniť kanál' : 'Založiť sieť'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-(--radius-control) bg-(--color-surface-sunken) p-3">
      {!maSiet && (
        <Field
          label="Názov siete"
          value={nazov}
          onChange={setNazov}
          hint="Najviac 16 znakov; nesie ho dataset siete."
        />
      )}

      <div className="flex flex-col gap-1.5">
        <label htmlFor="thread-kanal" className="text-sm font-medium text-(--color-text)">
          Kanál
        </label>
        <select
          id="thread-kanal"
          value={zvoleny}
          onChange={(e) => {
            setVyber(Number(e.target.value));
            setPytaBlizko(false);
          }}
          className="w-full rounded-(--radius-control) border border-(--color-border-strong) bg-(--color-surface) px-3 py-2 text-sm text-(--color-text)"
        >
          {KANALY.map((k) => (
            <option key={k} value={k}>
              {k}
              {zigbeeKanal !== null && Math.abs(k - zigbeeKanal) < 3 ? ' — blízko Zigbee' : ''}
              {TICHE.includes(k) ? ' — mimo hlavných Wi-Fi' : ''}
            </option>
          ))}
        </select>
      </div>

      {maSiet && (
        <p className="text-xs text-(--color-text-muted)">
          Zmena ide cez čakajúci dataset s časovačom, takže ju všetky uzly prevezmú naraz. Sieťový
          kľúč ani názov sa pritom nemenia — posiela sa len kanál.
        </p>
      )}

      {blizko && (
        <Notice tone="warn">
          <p className="font-medium">
            Zigbee beží na kanáli {zigbeeKanal} — zvolený kanál je bližšie než tri kanály.
          </p>
          <p className="mt-1">
            Tri kanály sú 15 MHz. Bližšie si dve rádiá zavadzajú, aj keď je Wi-Fi ticho.
          </p>
        </Notice>
      )}

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

      <div className="flex flex-wrap gap-2">
        <Button
          {...(blizko ? { variant: 'danger' as const } : {})}
          onClick={() => posli(pytaBlizko || blizko)}
          disabled={bezi}
        >
          {bezi ? 'Zapisujem…' : maSiet ? 'Zmeniť kanál' : 'Založiť sieť'}
        </Button>
        <Button variant="ghost" onClick={() => setOtvorene(false)} disabled={bezi}>
          Nechať
        </Button>
      </div>
    </div>
  );
}

/** Kanaly 802.15.4 v pasme 2,4 GHz. */
const KANALY = Array.from({ length: 16 }, (_, i) => 11 + i);
/** Kanaly najmenej v kolizii s beznym Wi-Fi; posudok pocita server. */
const TICHE = [15, 20, 25, 26];

/**
 * Prednastaveny kanal pre Thread.
 *
 * Z tichych kanalov ten, ktory je najdalej od Zigbee - aby sa spravna
 * volba nemusela hladat rucne.
 */
function odporucenyKanal(zigbeeKanal: number | null): number {
  if (zigbeeKanal === null) return 25;
  const vhodne = TICHE.filter((k) => Math.abs(k - zigbeeKanal) >= 3);
  return vhodne.at(-1) ?? 25;
}

/**
 * Otazka pred prepnutim.
 *
 * Prepnutie radia nie je nastavenie, ktore sa da hned vratit: kontajner
 * sluzby sa znovu vytvara a jej siet je pritom niekolko sekund mimo.
 * Pri Zigbee to navyse znamena, ze ovladace a senzory v tej chvili
 * nikam nehlasia - stlacene tlacidlo sa stratí.
 */
function Potvrdenie({
  volba,
  kluce,
  prebieha,
  naPotvrdenie,
  naZrusenie,
}: {
  readonly volba: { readonly sluzba: SluzbaRadia; readonly zariadenie: string };
  readonly kluce: readonly SeriovyKlucRadia[];
  readonly prebieha: boolean;
  readonly naPotvrdenie: () => void;
  readonly naZrusenie: () => void;
}): ReactNode {
  const simulacia = volba.zariadenie === SIMULACIA;
  // Ktore radio sluzba pouziva teraz - aby bolo vidno, co sa uvolni.
  const doteraz = kluce.find((k) => k.pouziva === volba.sluzba);

  return (
    <Notice tone="warn">
      <p className="font-medium">
        {simulacia
          ? 'Prepnúť border router na simulované rádio?'
          : `Prepnúť ${NAZOV_SLUZBY[volba.sluzba]} na toto rádio?`}
      </p>

      {!simulacia && <p className="mt-1 break-all font-mono text-xs">{volba.zariadenie}</p>}

      <p className="mt-2">
        {volba.sluzba === 'zigbee2mqtt' ? (
          <>
            Zigbee2MQTT sa znovu vytvorí a <strong>sieť bude pár desiatok sekúnd mimo</strong>.
            Stlačené tlačidlá a pohyb, ktoré medzitým prídu, sa stratia. Spárované zariadenia
            zostávajú — tie sú v sieti, nie v kontejneri.
          </>
        ) : (
          <>
            Border router sa znovu vytvorí. Dataset siete zostáva uložený, takže Thread sieť
            pokračuje tam, kde bola
            {simulacia ? ' — ale do éteru sa už nevysiela nič a nič sa nespáruje.' : '.'}
          </>
        )}
      </p>

      {doteraz !== undefined && doteraz.nazov !== volba.zariadenie && (
        <p className="mt-2 text-xs">
          Doterajšie rádio <span className="font-mono break-all">{doteraz.nazov}</span> sa tým
          uvoľní.
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="danger" onClick={naPotvrdenie} disabled={prebieha}>
          {prebieha ? 'Prepínam…' : 'Naozaj prepnúť'}
        </Button>
        <Button variant="ghost" onClick={naZrusenie} disabled={prebieha}>
          Nechať
        </Button>
      </div>
    </Notice>
  );
}

/**
 * Co stoji za upozornenie.
 *
 * Kody a cisla prichadzaju zo servera, vety su tu: server dodava fakty,
 * rozhranie ich pomenuje.
 */
function Varovania({ varovania }: { readonly varovania: readonly VarovanieRadii[] }): ReactNode {
  return (
    <div className="flex min-w-0 flex-col gap-3">
      {varovania.map((v, i) => (
        <Notice key={`${v.kod}-${i}`} tone={v.kod === 'kluc-chyba' ? 'danger' : 'warn'}>
          {v.kod === 'kluc-chyba' && (
            <>
              <p className="font-medium">
                {NAZOV_SLUZBY[v.sluzba ?? 'zigbee2mqtt']} má nastavené rádio, ktoré nie je
                pripojené.
              </p>
              <p className="mt-1 break-all">
                V nastavení je <code>{v.zariadenie}</code>, ale v bráne taký kľúč nie je. Služba ho
                nemá ako otvoriť — skontroluj, či je zapichnutý, prípadne vyber nižšie iný.
              </p>
            </>
          )}

          {v.kod === 'kanaly-blizko' && (
            <>
              <p className="font-medium">
                Zigbee a Thread sú na kanáloch {v.kanaly?.join(' a ')} — bližšie než tri kanály.
              </p>
              <p className="mt-1">
                Tri kanály sú 15 MHz. Bližšie si dve rádiá zavadzajú, aj keď je Wi-Fi ticho. Posuň
                radšej Thread: jeho kanál sa mení cez dataset, kdežto zmena kanála Zigbee je rušivý
                zásah — spiace zariadenia (tlačidlá, PIR) ju často nestihnú a treba ich spárovať
                znova.
              </p>
            </>
          )}

          {v.kod === 'wifi-prekryv' && (
            <>
              <p className="font-medium">
                {NAZOV_SLUZBY[v.sluzba ?? 'zigbee2mqtt']} beží na kanáli {v.kanal}, ktorý sa
                prekrýva s Wi-Fi {v.kanaly?.join(' a ')}.
              </p>
              <p className="mt-1">
                Každý kanál Wi-Fi je široký 20 MHz a prekryje štyri až päť kanálov 802.15.4.
              </p>
            </>
          )}

          {v.kod === 'infra-slucka' && (
            <>
              <p className="font-medium">
                Border router beží so skutočným rádiom, ale do siete hľadí len na slučku.
              </p>
              <p className="mt-1">
                Pri simulovanom rádiu je <code>lo</code> správne. So skutočným nie: Thread síce
                beží, ale border router nemá cez čo ohlasovať služby do domácej siete, takže
                zariadenia z LAN nikto nedosiahne — a presne to je jeho úloha. Nižšie v karte Thread
                vyber rozhranie, cez ktoré brána vidí sieť.
              </p>
            </>
          )}

          {v.kod === 'bez-agenta' && (
            <>
              <p className="font-medium">Agent prepínania sa neohlásil.</p>
              <p className="mt-1">
                Prepnúť rádio z rozhrania sa preto nedá. Ktoré rádio má služba používať, sa
                rozhoduje pri vzniku kontajnera, takže zmena potrebuje Docker — a brána k nemu
                prístup zámerne nemá. Robí to agent na hostiteľovi:{' '}
                <code>sudo deploy/install-updater.sh</code>.
              </p>
            </>
          )}
        </Notice>
      ))}
    </div>
  );
}

function KartaAgenta({
  stav,
  sviezi,
}: {
  readonly stav: StavAgentaRadii;
  readonly sviezi: boolean;
}): ReactNode {
  if (stav.kind !== 'znamy') return null;

  return (
    <Card className="flex min-w-0 flex-col gap-3 p-4 sm:p-5">
      <SectionTitle>Prepínanie</SectionTitle>

      {stav.prebieha !== null ? (
        <Notice tone="warn">
          <p className="font-medium">
            Prepína sa {NAZOV_SLUZBY[stav.prebieha.sluzba as SluzbaRadia] ?? stav.prebieha.sluzba}…
          </p>
          <p className="mt-1">
            Služba sa znovu vytvára. Kým to beží, ďalšie prepnutie sa neprijme — dva{' '}
            <code>docker compose up</code> naraz by si liezli do cesty.
          </p>
        </Notice>
      ) : (
        stav.poslednyVysledok !== null &&
        (sviezi ? (
          <Notice tone={stav.poslednyVysledok.ok ? 'ok' : 'danger'}>
            <p className="font-medium">
              {stav.poslednyVysledok.ok
                ? 'Posledné prepnutie prešlo.'
                : 'Posledné prepnutie zlyhalo.'}
            </p>
            <p className="mt-1 break-words">{stav.poslednyVysledok.sprava}</p>
          </Notice>
        ) : (
          /*
           * Starsi vysledok je HISTORIA, nie sprava.
           *
           * Agent ho zapise a sam ho nezahodi; ked sa stav medzitym
           * spravi inak, zlyhanie z minulosti by krizalo navzdy -
           * presne to sa stalo 2026-10-12.
           */
          <div className="flex min-w-0 flex-col gap-0.5 text-sm text-(--color-text-muted)">
            <span>
              Posledné prepnutie{' '}
              {new Date(stav.poslednyVysledok.skoncilaAt).toLocaleString('sk-SK')} —{' '}
              {stav.poslednyVysledok.ok ? 'prešlo' : 'zlyhalo'}
            </span>
            <span className="break-words text-xs text-(--color-text-subtle)">
              {stav.poslednyVysledok.sprava}
            </span>
          </div>
        ))
      )}

      <dl>
        <DataRow label="Nastavené pre Zigbee">
          <span className="break-all font-mono text-xs">{stav.nastavene.zigbee2mqtt ?? 'nič'}</span>
        </DataRow>
        <DataRow label="Nastavené pre Thread">
          <span className="break-all font-mono text-xs">{stav.nastavene.otbr ?? 'nič'}</span>
        </DataRow>
      </dl>
    </Card>
  );
}

/** Kanal Threadu, ked je znamy. */
function threadKanal(thread: StavThreadu): number | null {
  return thread.kind === 'znamy' ? (thread.dataset?.channel ?? null) : null;
}

function KartaZigbee({
  zigbee,
  threadKanal: kanalThreadu,
  jeSpravca,
  naObnovu,
}: {
  readonly zigbee: PrehladRadii['zigbee'];
  readonly threadKanal: number | null;
  readonly jeSpravca: boolean;
  readonly naObnovu: () => void;
}): ReactNode {
  return (
    <Card className="flex min-w-0 flex-col gap-3 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Zigbee</SectionTitle>
        <Badge tone={zigbee.online ? 'ok' : 'danger'}>
          {zigbee.online ? 'Beží' : 'Neohlásil sa'}
        </Badge>
      </div>

      <dl>
        <DataRow label="Koordinátor">{zigbee.coordinator ?? 'zatiaľ neohlásený'}</DataRow>
        <DataRow label="Kanál">
          <Kanal posudok={zigbee.posudok} />
        </DataRow>
        {zigbee.panId !== null && (
          <DataRow label="PAN ID">
            <span className="font-mono text-xs">
              0x{zigbee.panId.toString(16).padStart(4, '0')}
            </span>
          </DataRow>
        )}
        <DataRow label="Rádio">
          <span className="break-all font-mono text-xs">
            {/* To, co hlasi most sam o sebe - nie to, co je v nastaveni. */}
            {zigbee.serialPort ?? 'zatiaľ neohlásené'}
          </span>
        </DataRow>
        {zigbee.serialBaudrate !== null && (
          <DataRow label="Rýchlosť linky">{zigbee.serialBaudrate} Bd</DataRow>
        )}
      </dl>

      {jeSpravca && zigbee.online && (
        <KanalZigbee kanal={zigbee.channel} threadKanal={kanalThreadu} naObnovu={naObnovu} />
      )}
    </Card>
  );
}

/**
 * Zmena kanala Zigbee.
 *
 * Najtvrdsi zasah, ktory sa z rozhrania da vyvolat, a jedine miesto
 * v celom projekte, kde sa potvrdzuje PREPISANIM hodnoty, nie klikom.
 * Dovod: zariadenia napajane zo siete zmenu obvykle prevezmu, ale
 * spiace bateriove (tlacidla, PIR) ju casto nestihnu a treba ich
 * sparovat znova - teda obejst dom.
 */
function KanalZigbee({
  kanal,
  threadKanal: kanalThreadu,
  naObnovu,
}: {
  readonly kanal: number | null;
  readonly threadKanal: number | null;
  readonly naObnovu: () => void;
}): ReactNode {
  const [otvorene, setOtvorene] = useState(false);
  const [vyber, setVyber] = useState<number | undefined>(undefined);
  const [prepis, setPrepis] = useState('');
  const [bezi, setBezi] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [hotovo, setHotovo] = useState(false);
  const nacitanie = useApi((signal) => zigbee.prehlad(signal), [otvorene]);

  const zvoleny = vyber ?? odporucenyZigbee(kanalThreadu, kanal);
  const blizko = kanalThreadu !== null && Math.abs(zvoleny - kanalThreadu) < 3;
  const sedi = prepis.trim() === String(zvoleny);

  const posli = (): void => {
    setChyba(undefined);
    setBezi(true);
    void zigbee
      .zmenKanal(zvoleny, blizko)
      .then(() => {
        setOtvorene(false);
        setPrepis('');
        setHotovo(true);
        naObnovu();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Kanál sa nepodarilo zmeniť.');
      })
      .finally(() => setBezi(false));
  };

  if (!otvorene) {
    return (
      <div className="flex flex-col gap-2">
        {hotovo && (
          <Notice tone="warn">
            <p className="font-medium">Zmena kanála je zapísaná a most sa reštartuje.</p>
            <p className="mt-1">
              Reštart a rozoslanie po sieti trvajú spolu necelú minútu. Potom skontroluj batériové
              zariadenia — tlačidlá stlač, pred PIR sa prejdi. Čo sa neohlási, treba spárovať znova.
            </p>
          </Notice>
        )}
        <div>
          <Button variant="ghost" onClick={() => setOtvorene(true)}>
            Zmeniť kanál
          </Button>
        </div>
      </div>
    );
  }

  const bateriove = (nacitanie.data?.devices ?? []).filter((d) =>
    (d.powerSource ?? '').toLowerCase().includes('battery'),
  );

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-(--radius-control) bg-(--color-surface-sunken) p-3">
      <Notice tone="danger">
        <p className="font-medium">Zmena kanála je rušivý zásah.</p>
        <p className="mt-1">
          Zigbee má na to normu: most po reštarte rozošle po sieti oznámenie o novom kanáli a
          dvanásť sekúnd čaká, kým sa rozšíri. Zariadenia napájané zo siete ho obvykle prevezmú.{' '}
          <strong>Spiace batériové zariadenia ho často nestihnú</strong> a treba ich spárovať znova
          — fyzicky, pri každom z nich.
        </p>
      </Notice>

      {bateriove.length > 0 && (
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-sm font-medium text-(--color-text)">
            Batériové zariadenia v riziku ({bateriove.length})
          </p>
          <ul className="list-inside list-disc text-sm text-(--color-text-muted)">
            {bateriove.map((d) => (
              <li key={d.id} className="break-words">
                {d.name}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <label htmlFor="zigbee-kanal" className="text-sm font-medium text-(--color-text)">
          Nový kanál
        </label>
        <select
          id="zigbee-kanal"
          value={zvoleny}
          onChange={(e) => {
            setVyber(Number(e.target.value));
            setPrepis('');
          }}
          className="w-full rounded-(--radius-control) border border-(--color-border-strong) bg-(--color-surface) px-3 py-2 text-sm text-(--color-text)"
        >
          {KANALY.filter((k) => k !== kanal).map((k) => (
            <option key={k} value={k}>
              {k}
              {ZLL.includes(k) ? ' — ZLL' : ''}
              {kanalThreadu !== null && Math.abs(k - kanalThreadu) < 3 ? ' — blízko Threadu' : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-(--color-text-subtle)">
          Zigbee2MQTT odporúča kanály ZLL ({ZLL.join(', ')}) — mimo nich niektoré žiarovky nenájde
          dotykové párovanie.
        </p>
      </div>

      {blizko && (
        <Notice tone="warn">
          Thread beží na kanáli {kanalThreadu}. Tri kanály sú 15 MHz — bližšie si rádiá zavadzajú.
        </Notice>
      )}

      <Field
        label={`Na potvrdenie napíš číslo kanála (${zvoleny})`}
        value={prepis}
        onChange={setPrepis}
        hint="Zámerne sa to nedá odkliknúť jedným tlačidlom."
      />

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

      <div className="flex flex-wrap gap-2">
        <Button variant="danger" onClick={posli} disabled={bezi || !sedi}>
          {bezi ? 'Zapisujem…' : `Zmeniť na kanál ${zvoleny}`}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setOtvorene(false);
            setPrepis('');
          }}
          disabled={bezi}
        >
          Nechať
        </Button>
      </div>
    </div>
  );
}

/** Kanaly ZLL - dotykove parovanie hlada len na nich. */
const ZLL = [11, 15, 20, 25];

/**
 * Prednastaveny kanal pre Zigbee.
 *
 * Z kanalov ZLL ten, ktory je najdalej od Threadu a nie je to ten
 * sucasny. Kanal 11 sa prekryva s Wi-Fi 1, takze je posledna volba.
 */
function odporucenyZigbee(threadKanal: number | null, sucasny: number | null): number {
  const poradie = [15, 20, 25, 11];
  const vhodne = poradie.filter(
    (k) => k !== sucasny && (threadKanal === null || Math.abs(k - threadKanal) >= 3),
  );
  return vhodne[0] ?? 15;
}

function KartaThreadu({
  thread,
  zigbeeKanal,
  jeSpravca,
  rozhrania,
  infraIf,
  naObnovu,
}: {
  readonly thread: StavThreadu;
  readonly zigbeeKanal: number | null;
  readonly jeSpravca: boolean;
  readonly rozhrania: readonly string[];
  readonly infraIf: string | null;
  readonly naObnovu: () => void;
}): ReactNode {
  return (
    <Card className="flex min-w-0 flex-col gap-3 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Thread</SectionTitle>
        {thread.kind === 'znamy' && (
          <Badge tone={TON_STAVU_THREADU[thread.stav] ?? 'neutral'}>
            {POPIS_STAVU_THREADU[thread.stav] ?? thread.stav}
          </Badge>
        )}
        {thread.kind === 'nedostupny' && <Badge tone="danger">Neodpovedá</Badge>}
        {thread.kind === 'nenastavene' && <Badge tone="neutral">Nenastavený</Badge>}
      </div>

      {thread.kind === 'nenastavene' && (
        <p className="text-sm text-(--color-text-muted)">
          Adresa border routera nie je nastavená (<code>LOXLINK_OTBR_URL</code>), takže o Threade
          brána netvrdí nič — ani že beží, ani že nebeží.
        </p>
      )}

      {thread.kind === 'nedostupny' && (
        <Notice tone="danger">
          <p className="font-medium">Border router neodpovedá.</p>
          <p className="mt-1 break-words">{thread.chyba}</p>
          <p className="mt-1">
            Skontroluj ho príkazom <code>docker compose logs otbr</code>.
          </p>
        </Notice>
      )}

      {thread.kind === 'znamy' && (
        <>
          <dl>
            <DataRow label="Sieť">{thread.dataset?.networkName ?? 'ešte nevznikla'}</DataRow>
            <DataRow label="Kanál">
              <Kanal posudok={thread.posudok} />
            </DataRow>
            {thread.dataset?.panId !== undefined && (
              <DataRow label="PAN ID">
                <span className="font-mono text-xs">
                  0x{thread.dataset.panId.toString(16).padStart(4, '0')}
                </span>
              </DataRow>
            )}
            <DataRow label="Rozhranie do siete">{infraIf ?? 'nehlási sa'}</DataRow>
            <DataRow label="Firmvér rádia">
              <span className="break-all font-mono text-xs">
                {thread.verziaRadia ?? 'nehlási sa'}
              </span>
            </DataRow>
          </dl>

          {thread.dataset === null && (
            <p className="text-sm text-(--color-text-muted)">
              Sieť Thread ešte nevznikla — border router beží, ale nemá dataset. Nie je to chyba.
            </p>
          )}

          {thread.stav === 'detached' && (
            <Notice tone="warn">
              <p className="font-medium">Zásobník je zapnutý, ale uzol nie je v sieti.</p>
              <p className="mt-1">
                V tomto stave sa dataset nedá zapísať: aktívny prijme len vypnutý uzol a zmenu
                bežiacej siete len uzol, ktorý v nej je. Buď ešte len hľadá sieť, alebo žiadna nie
                je — vtedy zásobník vypni a sieť založ.
              </p>
            </Notice>
          )}

          {jeSpravca && rozhrania.length > 0 && (
            <RozhranieThreadu rozhrania={rozhrania} infraIf={infraIf} naObnovu={naObnovu} />
          )}

          {jeSpravca && <ZasobnikThreadu stav={thread.stav} naObnovu={naObnovu} />}

          {jeSpravca && (
            <SietThreadu
              maSiet={thread.dataset !== null}
              kanal={thread.dataset?.channel}
              zigbeeKanal={zigbeeKanal}
              naObnovu={naObnovu}
            />
          )}
        </>
      )}
    </Card>
  );
}

function Kanal({ posudok }: { readonly posudok: PosudokKanala | null }): ReactNode {
  if (posudok === null) return <span>neznámy</span>;
  const rusi = posudok.rusiaceWifi.length > 0;
  return (
    <span className={farbaTonu(rusi ? 'warn' : 'ok')}>
      {posudok.kanal}
      {rusi
        ? ` · prekrýva sa s Wi-Fi ${posudok.rusiaceWifi.join(' a ')}`
        : ' · mimo hlavných kanálov Wi-Fi'}
    </span>
  );
}

function RiadokKluca({
  kluc,
  jeSpravca,
  mozePrepnut,
  prepina,
  naVyber,
}: {
  readonly kluc: SeriovyKlucRadia;
  readonly jeSpravca: boolean;
  readonly mozePrepnut: boolean;
  readonly prepina: string | undefined;
  readonly naVyber: (sluzba: SluzbaRadia, zariadenie: string) => void;
}): ReactNode {
  return (
    <Card className="flex min-w-0 flex-col gap-3 p-3 sm:p-4">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="break-all font-mono text-xs">{kluc.nazov}</p>
          {kluc.uzol !== null && (
            <p className="mt-1 text-xs text-(--color-text-muted)">
              {/* Uzol sa po restarte moze precislovat; preto sa vybera podla nazvu. */}
              teraz ako <span className="font-mono">{kluc.uzol}</span>
            </p>
          )}
        </div>
        <Badge tone={kluc.pouziva === null ? 'neutral' : 'accent'}>
          {kluc.pouziva === null ? 'Voľné' : NAZOV_SLUZBY[kluc.pouziva]}
        </Badge>
      </div>

      {jeSpravca && (
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {(['zigbee2mqtt', 'otbr'] as const).map((sluzba) => {
              const uzPouziva = kluc.pouziva === sluzba;
              const beziTento = prepina === `${sluzba}:${kluc.nazov}`;
              /*
               * Kluc, ktory uz pouziva DRUHA sluzba, sa neponuka.
               *
               * Seriovy port sa da otvorit viackrat, takze nic neodmietne
               * samo: oba procesy si na linke rozsypu ramce a Zigbee siet
               * prestane komunikovat - vyzera to ako porucha USB klucu.
               * Presne to sa stalo 2026-10-09, ked rozhranie ponukalo
               * koordinator Zigbee aj pre border router. Server to uz
               * odmietne, tlacidlo tu preto vobec nema co robit.
               */
              if (kluc.pouziva !== null && !uzPouziva) return null;
              return (
                <Button
                  key={sluzba}
                  variant="ghost"
                  disabled={uzPouziva || !mozePrepnut || prepina !== undefined}
                  onClick={() => naVyber(sluzba, kluc.nazov)}
                >
                  {beziTento ? 'Prepínam…' : `Použiť pre ${NAZOV_SLUZBY[sluzba]}`}
                </Button>
              );
            })}
          </div>

          {kluc.pouziva !== null && (
            <p className="text-xs text-(--color-text-muted)">
              Toto rádio používa {NAZOV_SLUZBY[kluc.pouziva]}. Dať ho aj druhej službe sa nedá — dva
              procesy na jednej sériovej linke si rozsypú rámce. Ak ho chceš presunúť, najprv nastav{' '}
              {NAZOV_SLUZBY[kluc.pouziva]} na iné rádio
              {kluc.pouziva === 'otbr' ? ' alebo na simuláciu' : ''}.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * Simulovane radio.
 *
 * Border router sa da spustit aj bez hardveru - `otbr-agent` sa pripoji
 * na `ot-rcp` cez forkpty. Rozhranie REST je pritom to iste ako so
 * skutocnym radiom, takze sa da overit cela brana; merat pasmo sa
 * samozrejme neda.
 */
function KartaSimulacie({
  thread,
  agent,
  jeSpravca,
  prepina,
  naVyber,
}: {
  readonly thread: StavThreadu;
  readonly agent: StavAgentaRadii;
  readonly jeSpravca: boolean;
  readonly prepina: string | undefined;
  readonly naVyber: (sluzba: SluzbaRadia, zariadenie: string) => void;
}): ReactNode {
  if (thread.kind === 'nenastavene') return null;

  const vSimulacii = agent.kind === 'znamy' && agent.nastavene.otbr === SIMULACIA;
  const mozePrepnut = agent.kind === 'znamy' && agent.prebieha === null;

  return (
    <Card className="flex min-w-0 flex-col gap-3 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Simulované rádio</SectionTitle>
        {vSimulacii && <Badge tone="warn">Zapnuté</Badge>}
      </div>

      <p className="text-sm text-(--color-text-muted)">
        Border router vie bežať aj bez hardvéru — rozhranie je pritom to isté ako so skutočným
        rádiom, takže sa dá overiť celá brána. Do éteru však nevyšle nič: Thread zariadenie sa cez
        simuláciu nespáruje a pásmo sa ňou nedá merať.
      </p>

      {vSimulacii && (
        <Notice tone="warn">
          <p className="font-medium">Thread beží na simulovanom rádiu.</p>
          <p className="mt-1">
            Čo vidíš v karte Thread, je skutočný stav skutočného border routera — ale bez antény.
            Kým je toto zapnuté, nič sa nespáruje.
          </p>
        </Notice>
      )}

      {jeSpravca && !vSimulacii && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            disabled={!mozePrepnut || prepina !== undefined}
            onClick={() => naVyber('otbr', SIMULACIA)}
          >
            {prepina === `otbr:${SIMULACIA}` ? 'Prepínam…' : 'Prepnúť Thread na simuláciu'}
          </Button>
          <span className="text-xs text-(--color-text-muted)">
            Border router sa znovu vytvorí; potrvá to pár desiatok sekúnd.
          </span>
        </div>
      )}
    </Card>
  );
}
